import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { pricingConfig } from '../../data/pricing';
import type { BundleId } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { addonUnitPrice, betterBundleFor, bundleName, formatMoney, priceSystem } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { QuantityStepper } from '../ui/QuantityStepper';
type ExpansionOfferProps = {
  bundleId: BundleId;
  onAccept: (extras: number) => void;
  onDecline: () => void;
  onSwitch: (id: BundleId) => void;
};
const QUICK = [1, 2, 3, 4, 5];
export function ExpansionOffer({
  bundleId,
  onAccept,
  onDecline,
  onSwitch
}: ExpansionOfferProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [extras, setExtras] = useState(0); // never preselected
  const max = pricingConfig.addon.maxQty;
  const sys = priceSystem(bundleId, extras);
  const unit = pricingConfig.unitPrice;
  const addon = addonUnitPrice();
  const better = extras > 0 ? betterBundleFor(bundleId, extras) : null;
  const m = (n: number) => formatMoney(n, lang);
  return <div data-id="element-688" data-mp="3kh8xyxjvcoba">
      <h2 id="funnel-title" className="mt-3 font-display text-4xl font-semibold uppercase leading-none md:text-5xl" data-id="element-689" data-mp="3kgzhc0kprti9">
        {t('upsell.headline')}
      </h2>
      <p className="mt-4 text-lg text-white" data-id="element-690" data-mp="3bgb2j3nhxp4b">{t('upsell.sub')}</p>
      <p className="mt-2 text-titanium" data-id="element-691" data-mp="2ws6hvu4vv3iw">
        Need extra lighting for your steps, corners, spa, or other areas? You've unlocked special pricing on additional
        lights with your {bundleName(sys.bundle, lang)}.
      </p>

      <dl className="mt-8 grid grid-cols-3 border-y mf-hairline py-5" data-id="element-692" data-mp="2nsb2te87iodl">
        <div data-id="element-693" data-mp="2rt3d8astezbg">
          <dt className="text-xs text-titanium/80" data-id="element-694" data-mp="2rslixjppqv66">Standard price</dt>
          <dd className="mt-1 text-lg text-titanium line-through" data-id="element-695" data-mp="2rqqw1zuvsszg">{m(unit)}</dd>
        </div>
        <div data-id="element-696" data-mp="2dvf0xrctd0i0">
          <dt className="text-xs text-titanium/80" data-id="element-697" data-mp="2isf8y7t84dbr">Bundle-exclusive</dt>
          <dd className="mt-1 text-2xl font-semibold text-white" data-id="element-698" data-mp="2irnbssjza7jd">{m(addon)}</dd>
        </div>
        <div data-id="element-699" data-mp="odrpgyhohb6c">
          <dt className="text-xs text-titanium/80" data-id="element-700" data-mp="oeb70e3f1xju">You save per light</dt>
          <dd className="mt-1 text-lg font-semibold text-ice" data-id="element-701" data-mp="jjx65w5ziy7s">{m(unit - addon)}</dd>
        </div>
      </dl>

      <fieldset className="mt-7" data-id="element-702" data-mp="fkeod99gn396">
        <legend className="text-sm font-medium text-white" data-id="element-703" data-mp="aksacxlt5izb">Add lights</legend>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-704" data-mp="aip9tjn147gc">
          {QUICK.map(n => <button key={n} type="button" aria-pressed={extras === n} onClick={() => setExtras(extras === n ? 0 : n)} className={`h-11 min-w-[56px] rounded-md border px-3 font-display text-lg font-semibold transition-colors duration-150 ${extras === n ? 'border-marine bg-marine text-white' : 'mf-hairline text-titanium hover:border-titanium/50 hover:text-white'}`} data-id="element-705" data-mp="178yihuhxutic">
              +{n}
            </button>)}
          <span className="mx-2 hidden text-sm text-titanium/60 sm:inline" data-id="element-706" data-mp="33xtxnyd5okpl">or</span>
          <QuantityStepper value={extras} onChange={setExtras} min={0} max={max} label="additional lights" data-id="element-707" data-mp="38wohn3mv0fba" />
        </div>
        <p className="mt-2 text-xs text-titanium/60" data-id="element-708" data-mp="3dv83z55d7t46">Up to {max} additional lights per system.</p>
      </fieldset>

      <div className="mt-7 rounded-xl bg-deep/60 p-5" data-id="element-709" data-mp="3injfz6ddr5d3">
        <div className="flex items-baseline justify-between text-sm text-titanium" data-id="element-710" data-mp="3ioaybfhyv6lf">
          <span data-id="element-711" data-mp="3mq5zmjzwdjz7">Total lights</span>
          <span className="text-white" data-id="element-712" data-mp="3rmn08doa9qt6">
            {sys.bundle.qty} + {sys.extras} = <strong className="text-lg" data-id="element-713" data-mp="3rozh0nib1v4n">{sys.lights}</strong>
          </span>
        </div>
        <div className="mt-2 flex items-baseline justify-between text-sm text-titanium" data-id="element-714" data-mp="esfsi2blyvu">
          <span data-id="element-715" data-mp="5f6jxp0swool">Additional savings</span>
          <span className="text-ice" data-id="element-716" data-mp="228rn6scbnw39">{m(sys.extrasSavings)}</span>
        </div>
        <div className="mt-3 flex items-baseline justify-between border-t mf-hairline pt-3" data-id="element-717" data-mp="277b9wuyqpbwo">
          <span className="text-sm text-titanium" data-id="element-718" data-mp="2795ug863n208">Updated order total</span>
          <span className="font-display text-3xl font-semibold" data-id="element-719" data-mp="1h7ms9ehppbt">{m(sys.total)}</span>
        </div>
      </div>

      {better && <div className="mt-4 rounded-xl border border-marine/40 bg-marine/[0.08] p-5" data-id="element-720" data-mp="ajqkyg7bzdrq">
          <p className="font-semibold text-white" data-id="element-721" data-mp="ahcs8e04idnc">The {bundleName(better.bundle, lang)} is a better deal.</p>
          <p className="mt-1 text-sm text-titanium" data-id="element-722" data-mp="3e0fkvjcerp8f">
            {better.bundle.qty} lights for {m(better.total)}
            {better.saves > 0 ? ` — ${m(better.saves)} less` : ''}
            {better.extraLights > 0 ? `, with ${better.extraLights} more light${better.extraLights > 1 ? 's' : ''}` : ''}.
          </p>
          <button type="button" onClick={() => onSwitch(better.bundle.id)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ice hover:text-white" data-id="element-723" data-mp="3iz0lvkzhicxx">
            Switch to the {bundleName(better.bundle, lang)} <ArrowRightIcon className="h-4 w-4" data-id="element-724" data-mp="2q90nm4zhny41" />
          </button>
        </div>}

      <div className="mt-8 flex flex-col gap-3" data-id="element-725" data-mp="15qpvvnzxgzqq">
        <button type="button" disabled={extras === 0} onClick={() => onAccept(extras)} className={`${buttonClasses('primary', 'lg')} w-full`} data-id="element-726" data-mp="1ajspfw4pg96x">
          {extras === 0 ? 'Select lights to add' : t('upsell.accept')}
        </button>
        <button type="button" onClick={onDecline} className={`${buttonClasses('secondary')} w-full`} data-id="element-727" data-mp="1ph31p7p69j3p">
          {t('upsell.decline')}
        </button>
      </div>
    </div>;
}